import React from 'react'
import { API_RESORT } from '@constants'
import { serverUrl } from '@utils/MkEnv'
import { IResort } from '@resort/types'
import { useSiteContext } from '@contexts/MkSiteContext'
import { transformResortMedia } from '@utils/transformResortMedia'
import { useAuthHeaders } from '@shared/hooks/useAuthHeaders'

/** Set when leaving booking (e.g. timer expired) so the resort page can reopen Find availability. */
export type SuiteAvailabilityPendingIntent = {
  open: true
  /** If set, only that resort’s detail page consumes the intent. */
  resortKey?: string
}

export interface ResortPageContextType {
  evaluateResortKey: (resortKey: string) => void
  loading: boolean
  resortError: { raw?: Error; message?: string }
  resort: IResort
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
  pendingSuiteAvailabilityIntent: SuiteAvailabilityPendingIntent | null
  requestOpenAfterBookingExit: (resortKeyFromDraft?: string) => void
  clearPendingSuiteAvailabilityIntent: () => void
}

export const ResortPageContext = React.createContext<ResortPageContextType | undefined>(undefined)

export const useResortPage = () => {
  const context = React.useContext(ResortPageContext)
  if (!context) {
    throw new Error('useResortPage must be used within a ResortPageProvider')
  }
  return context
}

interface ResortPageProviderProps {
  children: React.ReactNode
}

export const ResortPageProvider: React.FC<ResortPageProviderProps> = ({ children }) => {
  const [resortKey, setResortKey] = React.useState<string>('')
  const [resort, setResort] = React.useState<IResort>({} as IResort)
  const authHeaders = useAuthHeaders()
  const [loading, setLoading] = React.useState(true)
  const [resortError, setResortError] = React.useState<{ raw?: Error; message?: string }>({})
  const [pendingSuiteAvailabilityIntent, setPendingSuiteAvailabilityIntent] = React.useState<SuiteAvailabilityPendingIntent | null>(null)
  const { themeBrand: brand } = useSiteContext()

  const requestOpenAfterBookingExit = React.useCallback((resortKeyFromDraft?: string) => {
    setPendingSuiteAvailabilityIntent({ open: true, resortKey: resortKeyFromDraft })
  }, [])

  const clearPendingSuiteAvailabilityIntent = React.useCallback(() => {
    setPendingSuiteAvailabilityIntent(null)
  }, [])

  const evaluateResortKey = React.useCallback((resortKeyInQuestion: string) => {
    if (!resortKeyInQuestion || resortKeyInQuestion.length === 0) {
      throw new Error('Resort key cannot be empty')
    }
    setResortKey(resortKeyInQuestion)
  }, [])

  React.useEffect(() => {
    if (!resortKey) return
    const fetchResort = async () => {
      try {
        setLoading(true)
        const response = await fetch(serverUrl + API_RESORT(brand, resortKey), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders,
          },
        })
        if (!response.ok) throw new Error('Failed to fetch resort data')
        const data = await response.json()

        // Transform legacy format (primaryImage + images) to new media array structure
        const transformedData = transformResortMedia(data)

        setResort(transformedData)
      } catch (err) {
        setResortError({
          raw: err instanceof Error ? err : new Error(String(err)),
          message: err instanceof Error ? err.message : String(err),
        })
      } finally {
        setLoading(false)
      }
    }
    fetchResort()
  }, [authHeaders, brand, resortKey])

  const resortPageContextValue = React.useMemo(
    () => ({
      evaluateResortKey,
      loading,
      resort,
      resortError,
      setLoading,
      pendingSuiteAvailabilityIntent,
      requestOpenAfterBookingExit,
      clearPendingSuiteAvailabilityIntent,
    }),
    [evaluateResortKey, loading, resort, resortError, pendingSuiteAvailabilityIntent, requestOpenAfterBookingExit, clearPendingSuiteAvailabilityIntent],
  )

  return <ResortPageContext.Provider value={resortPageContextValue}>{children}</ResortPageContext.Provider>
}
